import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./NewModal.module.css";

const NewModal = ({
  isOpen,
  onClose,
  title = "Title",
  children,
  height = "80%",
  maxHeight = "90vh",
  bodyMaxHeight = "70vh",
  bodyPadding,
  bodyStyle,
  showCloseBtn = true,
  // "bottom" (default) renders the × below the sheet; "top" renders it above;
  // "inside" renders it at the top-right corner inside the sheet.
  closeBtnPosition = "bottom",
  // Optional node rendered ABOVE the sheet (over the backdrop), e.g. a floating
  // card that should sit above the bottom sheet rather than scroll inside it.
  topContent = null,
  // Optional overlay z-index override. Default (CSS) sits just below the
  // checkout/action footer; pass a higher value for modals that must cover
  // EVERYTHING, e.g. the "keep talking?" end-call confirm over the call banner.
  overlayZIndex,
  // Opt-in: title ke neeche poori chaudai ki grey line ki jagah beech me chhoti
  // purple gradient line. Unset → baaki sab modals pehle jaise.
  titleAccent = false,
}) => {
  // Keep the sheet mounted through its close animation: isOpen → true renders
  // instantly; isOpen → false flips `closing` on, plays the slide-down + fade,
  // then unmounts after the animation finishes.
  const [render, setRender] = useState(isOpen);
  const [closing, setClosing] = useState(false);
  const overlayRef = React.useRef(null);

  useEffect(() => {
    if (isOpen) {
      setRender(true);
      setClosing(false);
      return undefined;
    }
    if (render) {
      setClosing(true);
      const t = setTimeout(() => {
        setRender(false);
        setClosing(false);
      }, 220); // matches the exit animation duration
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Lock background scroll while the sheet is open. Locks BOTH <html> and
  // <body> (covers apps that scroll on either) and pins the body with a
  // negative-top offset so iOS can't momentum-scroll behind the sheet. The
  // original scroll position is captured and restored on close (no jump).
  useEffect(() => {
    if (!isOpen) return undefined;

    const scrollY = window.scrollY || window.pageYOffset || 0;
    const { body } = document;
    const html = document.documentElement;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
    };

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";

    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.width = prev.bodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  // Bulletproof scroll-block: swallow touch/wheel gestures on the overlay so the
  // page behind can never scroll (works even if something scrolls on a nested
  // container, not <body>). Scrolling INSIDE the sheet body is still allowed.
  useEffect(() => {
    if (!render) return undefined;
    const overlay = overlayRef.current;
    if (!overlay) return undefined;
    // Use getElementsByClassName (takes the class name literally) instead of
    // querySelector — CSS-module hashes can contain characters like "+" that are
    // invalid inside a CSS selector and would throw a SyntaxError.
    const scrollable = overlay.getElementsByClassName(styles.bottommodalbody)[0];
    const block = (e) => {
      if (scrollable && scrollable.contains(e.target)) return; // allow inner scroll
      e.preventDefault();
    };
    overlay.addEventListener("touchmove", block, { passive: false });
    overlay.addEventListener("wheel", block, { passive: false });
    return () => {
      overlay.removeEventListener("touchmove", block);
      overlay.removeEventListener("wheel", block);
    };
  }, [render]);

  if (!render) return null;

  // Mount at <body> so route containers and fixed app chrome cannot create a
  // stacking context above the backdrop or the optional floating plan card.
  return createPortal(
    <div
      ref={overlayRef}
      className={`${styles.bottommodaloverlay} ${closing ? styles.overlayClosing : ""}`}
      style={overlayZIndex != null ? { zIndex: overlayZIndex } : undefined}
    >
      {/* Cross Button ABOVE the sheet (closeBtnPosition="top"). */}
      {showCloseBtn && closeBtnPosition === "top" && (
        <button
          className={`${styles.overlayCloseBtn} ${styles.overlayCloseBtnTop}`}
          onClick={onClose}
          aria-label="Close modal"
        >
          ×
        </button>
      )}

      {/* Floating plan cards need their own root layer so they can sit above
          persistent app controls such as the Global Call Banner. */}
      {topContent && createPortal(topContent, document.body)}

      {/* Modal Content */}
      <div
        className={`${styles.bottommodalcontent} ${closing ? styles.contentClosing : ""}`}
        style={{ height, maxHeight }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cross Button INSIDE the sheet, top-right (closeBtnPosition="inside"). */}
        {showCloseBtn && closeBtnPosition === "inside" && (
          <button
            className={styles.overlayCloseBtnInside}
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        )}

        {/* Pass title={null} to render a headerless modal. */}
        {title !== null && (
          <div className={`${styles.bottommodalheader} ${titleAccent ? styles.headerAccent : ""}`}>
            <h2 className={styles.bottommodaltitle}>{title}</h2>
          </div>
        )}

        <div
          className={styles.bottommodalbody}
          style={{
            ...(bodyPadding ? { padding: bodyPadding } : {}),
            maxHeight: bodyMaxHeight,
            ...(bodyStyle || {}),
          }}
        >{children}</div>
      </div>

      {/* Cross Button BELOW the sheet (default). */}
      {showCloseBtn && closeBtnPosition === "bottom" && (
        <button
          className={styles.overlayCloseBtn}
          onClick={onClose}
          aria-label="Close modal"
        >
          ×
        </button>
      )}
    </div>,
    document.body
  );
};

export default NewModal;
